import { noop } from 'lodash/fp';
import { RefObject, useEffect, useState } from 'react';

import getIsServer from '../../utils/get-is-server';

// Adapted from https://usehooks-ts.com/react-hook/use-intersection-observer

type UseIntersectionObserverOptions = {
  root?: Element | Document | null;
  rootMargin?: string;
  threshold?: number | number[];
  freezeOnceVisible?: boolean;
};

type IntersectionReturn = {
  isSupported: boolean;
  entry?: IntersectionObserverEntry;
};
const useIntersectionObserver = (
  elementRef: RefObject<Element>,
  { threshold = 0, root = null, rootMargin = '0%', freezeOnceVisible = false }: UseIntersectionObserverOptions = {},
): IntersectionReturn => {
  const isServer = getIsServer();
  const hasIOSupport = !isServer && Boolean(window.IntersectionObserver);

  const [entry, setEntry] = useState<IntersectionObserverEntry>();

  const isFrozen = (entry?.isIntersecting ?? false) && freezeOnceVisible;

  const updateEntry: (entries: IntersectionObserverEntry[]) => void = ([newEntry]) => {
    setEntry(newEntry);
  };

  useEffect(() => {
    const node = elementRef.current;
    if (!hasIOSupport || isFrozen || !node) {
      return noop;
    }

    const observerParams = { root, rootMargin, threshold };
    const observer = new IntersectionObserver(updateEntry, observerParams);
    observer.observe(node);

    return () => observer.disconnect();
  }, [elementRef, hasIOSupport, isFrozen, root, rootMargin, threshold]);

  return { entry, isSupported: hasIOSupport };
};

export default useIntersectionObserver;
